import { useEffect, useState } from "react";
import api from "../lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Star, Check, X, EyeOff, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["Pending", "Approved", "Rejected", "Hidden"];

const STATUS_COLORS = {
  Pending: "#FACC15",
  Approved: "#A3E635",
  Rejected: "#F87171",
  Hidden: "#94A3B8",
};

function Stars({ value }) {
  return (
    <div className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} size={14} className={i <= value ? "text-yellow-400" : "text-slate-300"} fill={i <= value ? "currentColor" : "none"} />
      ))}
    </div>
  );
}

export default function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = () => {
    setLoading(true);
    const url = filter === "all" ? "/admin/reviews" : `/admin/reviews?status=${encodeURIComponent(filter)}`;
    api
      .get(url)
      .then((r) => setReviews(r.data || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const updateReview = async (id, payload) => {
    try {
      await api.patch(`/admin/reviews/${id}`, payload);
      toast.success("Review updated");
      // Re-fetch: approving a genuine review can auto-hide sibling sample
      // reviews for the same product server-side, so a full reload keeps
      // every row (not just the one clicked) in sync with the DB.
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update review");
    }
  };

  const deleteReview = async (id) => {
    if (!window.confirm("Delete this review permanently?")) return;
    try {
      await api.delete(`/admin/reviews/${id}`);
      toast.success("Review deleted");
      setReviews((prev) => prev.filter((r) => r.id !== id));
    } catch (_) {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="admin-reviews-page">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
        <div>
          <h1 className="font-display text-3xl font-bold">Reviews</h1>
          <p className="text-sm text-slate-600">Moderate customer reviews and manage Verified Purchase badges.</p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-48" data-testid="reviews-status-filter">
            <SelectValue placeholder="Filter status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-3" data-testid="reviews-list">
        {loading ? (
          <div className="text-center text-slate-500 py-10">Loading…</div>
        ) : reviews.length === 0 ? (
          <div className="text-center text-slate-500 py-10 neo-card bg-white" data-testid="reviews-empty">No reviews found.</div>
        ) : (
          reviews.map((r) => (
            <div key={r.id} className="neo-card p-4 bg-white" data-testid={`review-row-${r.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{r.product_name}</div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="font-semibold text-sm">{r.display_name}</span>
                    {r.is_sample && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded border-2 border-slate-900" style={{ background: "#FDE047" }}>
                        SAMPLE
                      </span>
                    )}
                    {r.verified_purchase && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded border-2 border-slate-900 inline-flex items-center gap-1" style={{ background: "#A3E635" }}>
                        <ShieldCheck size={10} /> Verified
                      </span>
                    )}
                  </div>
                  <Stars value={r.rating} />
                </div>
                <span
                  className="text-xs font-bold px-2 py-1 rounded-md whitespace-nowrap"
                  style={{ background: STATUS_COLORS[r.status], border: "2px solid #0F172A" }}
                  data-testid={`review-status-${r.id}`}
                >
                  {r.status}
                </span>
              </div>
              <p className="text-sm text-slate-700 mt-2">{r.review_text}</p>
              {(r.email || r.order_number) && (
                <div className="text-xs text-slate-500 mt-1">
                  {r.email && <span>Email: {r.email} </span>}
                  {r.order_number && <span>• Order: {r.order_number}</span>}
                </div>
              )}
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  onClick={() => updateReview(r.id, { status: "Approved" })}
                  className="neo-btn px-3 py-1.5 text-xs font-bold text-white inline-flex items-center gap-1"
                  style={{ background: "#34D399" }}
                  data-testid={`approve-review-${r.id}`}
                >
                  <Check size={12} /> Approve
                </button>
                <button
                  onClick={() => updateReview(r.id, { status: "Rejected" })}
                  className="neo-btn px-3 py-1.5 text-xs font-bold text-white inline-flex items-center gap-1"
                  style={{ background: "#F87171" }}
                  data-testid={`reject-review-${r.id}`}
                >
                  <X size={12} /> Reject
                </button>
                <button
                  onClick={() => updateReview(r.id, { status: "Hidden" })}
                  className="neo-btn px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1 bg-white"
                  data-testid={`hide-review-${r.id}`}
                >
                  <EyeOff size={12} /> Hide
                </button>
                <button
                  onClick={() => updateReview(r.id, { verified_purchase: !r.verified_purchase })}
                  className="neo-btn px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1 bg-white"
                  data-testid={`toggle-verified-review-${r.id}`}
                >
                  <ShieldCheck size={12} /> {r.verified_purchase ? "Unmark Verified" : "Mark Verified"}
                </button>
                <button
                  onClick={() => deleteReview(r.id)}
                  className="neo-btn px-3 py-1.5 text-xs font-bold text-white inline-flex items-center gap-1"
                  style={{ background: "#0F172A" }}
                  data-testid={`delete-review-${r.id}`}
                >
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
